import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, BadgeCheck, Loader2, ArrowRight, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Generic product waitlist modal. Accepts a `config` object describing the
 * product, copy, and which fields to show. Submits to /api/waitlist/signup
 * with the product key.
 */
const WaitlistModal = ({ open, onClose, config }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    orcid: "",
    affiliation: "",
    research_interests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Reset state whenever modal opens for a different product
  useEffect(() => {
    if (open) {
      setDone(false);
      setError("");
    }
  }, [open, config?.productKey]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist/signup`, {
        ...form,
        product: config.productKey,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose?.();
    setTimeout(() => {
      setDone(false);
      setError("");
      setForm({
        name: "",
        email: "",
        orcid: "",
        affiliation: "",
        research_interests: "",
      });
    }, 250);
  };

  if (!config) return null;
  const accent = config.accent || "#00BCD4";
  const showOrcid = config.showOrcid !== false;
  const showInterests = config.showInterests !== false;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="waitlist-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="waitlist-modal"
          >
            {/* Header bar */}
            <div className="relative bg-gray-900 px-6 py-5">
              <button
                onClick={handleClose}
                className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
                aria-label="Close"
                data-testid="waitlist-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] mb-2">
                <span
                  className="px-1.5 py-0.5 rounded-sm text-gray-900"
                  style={{ backgroundColor: accent === "#111827" ? "#fff" : accent }}
                >
                  {config.code}
                </span>
                <span className="text-white/60">·</span>
                <span className="text-white/60">{config.eyebrow || "Waitlist"}</span>
              </div>
              <h2 className="text-2xl font-light text-white tracking-tight">
                {config.title}
              </h2>
              <p className="text-sm text-white/70 font-light mt-1.5 leading-relaxed">
                {config.subtitle}
              </p>
            </div>

            {/* Body */}
            <div className="p-6 sm:p-7">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-6"
                  data-testid="waitlist-success"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                    <BadgeCheck className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {config.successTitle || "You're on the list"}
                  </h3>
                  <p className="text-sm text-gray-600 font-light leading-relaxed max-w-sm mx-auto">
                    {config.successBody ||
                      "We've received your details. The team will reach out soon."}
                  </p>
                  <button
                    onClick={handleClose}
                    className="mt-6 inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
                  >
                    Close
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="waitlist-form">
                  <Field
                    label="Name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Prof. Jane Doe"
                    testid="waitlist-name"
                  />
                  <Field
                    label="Email"
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane.doe@university.edu"
                    icon={<Mail className="w-3.5 h-3.5" />}
                    testid="waitlist-email"
                  />
                  {showOrcid && (
                    <Field
                      label="ORCID iD"
                      value={form.orcid}
                      onChange={update("orcid")}
                      placeholder="0000-0000-0000-0000"
                      hint="Optional — used to learn your research signature"
                      testid="waitlist-orcid"
                    />
                  )}
                  <Field
                    label="Affiliation"
                    value={form.affiliation}
                    onChange={update("affiliation")}
                    placeholder="Keimyung University, College of Medicine"
                    testid="waitlist-affiliation"
                  />
                  {showInterests && (
                    <div>
                      <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">
                        Research Interests
                      </label>
                      <textarea
                        value={form.research_interests}
                        onChange={update("research_interests")}
                        placeholder="e.g. medical imaging, brain–computer interfaces, affective computing"
                        rows={2}
                        className="w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors resize-none font-light"
                        data-testid="waitlist-interests"
                      />
                    </div>
                  )}

                  {error && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="waitlist-error">
                      {error}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400">
                      We never share your email.
                    </span>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 bg-gray-900 hover:bg-black text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="waitlist-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          {config.submitLabel || "Join the waitlist"}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({
  label,
  required,
  type = "text",
  value,
  onChange,
  placeholder,
  hint,
  icon,
  testid,
}) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-gray-600 block mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {icon}
        </span>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${icon ? "pl-9" : "px-3"} pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-colors font-light`}
        data-testid={testid}
      />
    </div>
    {hint && <p className="text-[10px] text-gray-400 mt-1 font-light">{hint}</p>}
  </div>
);

export default WaitlistModal;
